import './styles/globals.css';
import 'video.js/dist/video-js.css';
import './styles/videojs-skin.css';

import u from 'umbrellajs';
import { RemoteClient, LocalClient, ChaosClient, type Client } from './client';
import {
    PlaylistClass,
    type Playlist,
    type PlaylistLogoPosition,
} from './playlist';
import {
    VideoLayerClass,
    type VideoLayer,
    type VideoLayerState,
    AudioLayerClass,
    type AudioLayer,
} from './layer';
import env from './env';

const numberOfLayers = 3;

const videoLayers: VideoLayer[] = [];
let audioLayer: AudioLayer | null = null;

const main = async function () {
    let client: Client;

    const params = new URLSearchParams(document.location.search);
    const debug = params.get('debug') == 'true' || env.debug;

    try {
        if (env.local) {
            client = new LocalClient(
                env.localStreamingUrl,
                env.localStreamingToken,
            );
        } else {
            const cid = params.get('cid') || env.cid;
            const pid = params.get('pid') || env.pid;
            const user = params.get('user') || env.user;
            const pass = params.get('pass') || env.pass;

            client = new RemoteClient({
                user,
                pass,
                cid,
                pid,
            });
        }

        client = new ChaosClient(client);
    } catch (error) {
        console.error('failed to initialize client');
        return;
    }

    const videoLayersElm = u('#videoLayers').empty();

    const playlist = new PlaylistClass(client);
    playlist.onNewPlaylist((playlist: Playlist) => {
        audioLayer?.setSource('#audio', playlist.audio_url);

        for (let i = 0; i < numberOfLayers; i++) {
            if (!videoLayers[i]) {
                continue;
            }
            videoLayers[i].setTemplate(playlist.template_name);
        }
    });
    await playlist.refresh();

    if (playlist.playlist.display_safearea) {
        const safearea = u('#safearea').first();
        if (safearea) {
            (safearea as HTMLElement).style.display = 'block';
        }
    }

    if (playlist.playlist.logo) {
        const positionStyles: { [key in PlaylistLogoPosition]: string } = {
            'top-right': 'top:0; right:0;',
            'top-left': 'top:0; left:0;',
            'bottom-right': 'bottom:0; right:0',
            'bottom-left': 'bottom:0; left:0',
        };

        const logo = playlist.playlist.logo;
        const positionStyle = positionStyles[logo.position];
        const margin = logo.margin || '';
        const width = logo.width || '30vw';
        const image = `${logo.image}`;

        videoLayersElm.append(
            `<div style="position:absolute; opacity:1; z-index:999; margin:${margin}; ${positionStyle}"><image src="${image}" alt="Logo" style="width:${width}"/></div>`,
        );
    }

    const makeid = function (length: number) {
        const characters =
            'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;

        let result = '#';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(
                Math.floor(Math.random() * charactersLength),
            );
        }
        return result;
    };

    const switchToNextLayer = async function (context: string) {
        const inactive: VideoLayer[] = [];
        let current: VideoLayer | null = null;
        let pivot: number = -1;

        // Get the currently active layer and push all other layers to the inactive list.
        for (let i = 0; i < videoLayers.length; i++) {
            if (!videoLayers[i].isActive()) {
                inactive.push(videoLayers[i]);
            } else {
                current = videoLayers[i];
                pivot = i;
            }
        }

        console.log(
            context,
            'layers: inactive',
            inactive.length,
            'pivot',
            pivot,
        );

        if (inactive.length === 0) {
            console.warn(
                context,
                'no inactive layers, nothing to switch to',
                inactive.length,
            );
            return;
        }

        // Rotate the inactive list such that the layer after the active layer is the first in the array.
        for (let i = 0; i < pivot; i++) {
            inactive.unshift(inactive.pop() as VideoLayer);
        }

        // Check for layers in unknown state.
        for (let i = 0; i < inactive.length; i++) {
            if (inactive[i].getState() != 'unknown') {
                continue;
            }

            const source = await playlist.getNextContent();

            console.warn(
                context,
                inactive[i].getStatus().name,
                'unknown layer state -> setting new source',
                source?.id,
            );

            await inactive[i].setSource(context, source);
        }

        if (current === null) {
            // If there's no active layer, activate the next playing layer.
            for (let i = 0; i < inactive.length; i++) {
                if (inactive[i].getState() !== 'playing') {
                    continue;
                }

                current = inactive.splice(i, 1)[0];

                console.warn(
                    context,
                    'no active layer, choosing',
                    current.getStatus().name,
                );

                // Activate the layer and move it to the front z-index. All other layers will be moved to the back.
                current.setActive(true, numberOfLayers * 10);
                for (let i = 0; i < inactive.length; i++) {
                    inactive[i].setActive(false, (inactive.length - i) * 10);
                }

                break;
            }

            return;
        } else {
            let next: VideoLayer | null = null;

            // Find the next playing layer.
            for (let i = 0; i < inactive.length; i++) {
                if (inactive[i].getState() !== 'playing') {
                    continue;
                }

                next = inactive.splice(i, 1)[0];

                break;
            }

            if (Math.random() > 1 - env.chaosNoNextLayer) {
                console.log(
                    context,
                    'chaos: no next layer to switch to available',
                );
                next = null;
            }

            if (next === null) {
                console.warn(
                    context,
                    'no playing layer available to switch to',
                );

                // What to do?
                // Iterate through all inactive layers and set a new source. This will cause a state change.
                for (let i = 0; i < inactive.length; i++) {
                    await inactive[i].setSource(
                        context,
                        await playlist.getNextContent(),
                    );
                }

                // The currently playing layer already finished by timeout and will not trigger again and will keep
                // playing if not forcefully finished.
                current.forceFinish();

                return;
            }

            const previous = current;

            console.log(
                context,
                `switching layers ${current.getStatus().name} -> ${
                    next.getStatus().name
                }`,
            );

            // Deactivate the current layer and lift it above the top layer for the transition.
            current.setActive(false, (numberOfLayers + 1) * 10, async () => {
                // Set a new source for the previously active layer.
                await previous.setSource(
                    context,
                    await playlist.getNextContent(),
                );

                // Put the previously active layer at the end of the inactive layers and adjust the z-indexes.
                inactive.push(previous);
                for (let i = 0; i < inactive.length; i++) {
                    inactive[i].setActive(false, (inactive.length - i) * 10);
                }
            });

            // Activate the next layer.
            if (next.hasAudio()) {
                audioLayer?.setActive(false);
            } else {
                audioLayer?.setActive(true);
            }
            next.setActive(true, numberOfLayers * 10);
        }

        console.log(context, 'done');
        console.log('ping');
    };

    const onState = async function (
        id: number,
        name: string,
        src: string,
        state: VideoLayerState,
    ): Promise<void> {
        const context = makeid(5);

        console.log(
            context,
            name,
            `state: pid=${id}, src=${src}, state=${state}`,
        );

        if (state === 'playing') {
            // Check if there's any active layer. If yes, do nothing and return. Otherwise, switch to the next active layer.
            for (let i = 0; i < videoLayers.length; i++) {
                if (videoLayers[i].isActive()) {
                    return;
                }
            }

            console.log(
                context,
                "switching layer because there's no active player",
            );

            await switchToNextLayer(context);

            return;
        }

        // Wenn der state nicht "playing" ist, dann ist er entweder "finished" oder "error", also Zeit zu wechseln

        console.log(context, name, 'layer active', videoLayers[id].isActive());

        // Wenn der Layer aktiv ist, dann muss jetzt zum nächsten Layer gewechselt werden
        if (videoLayers[id].isActive()) {
            console.log(context, name, 'switching to next layer');
            await switchToNextLayer(context);
            return;
        }

        console.log(context, name, 'setting new source');

        // Ein inaktiver Layer hat einen Fehler gemeldet => neuen Content laden
        await videoLayers[id].setSource(
            context,
            await playlist.getNextContent(),
        );
    };

    // setup layers
    const videoLayerTemplate = u('#videoLayerTemplate');

    console.log('layertemplate', videoLayerTemplate);

    for (let i = 0; i < numberOfLayers; i++) {
        let name = 'pl' + i;

        let layer = videoLayerTemplate.clone().find('.qwertz-layer');
        let overlay = layer.find('.qwertz-overlay');
        let player = layer.find('.qwertz-player');

        layer.attr('id', name);

        let zIndex = numberOfLayers * 10 - i * 10;

        let htmlplayer = player.first() as HTMLElement;
        htmlplayer.style.zIndex = zIndex + '';

        let htmloverlay = overlay.first() as HTMLElement;
        htmloverlay.style.zIndex = zIndex + 1 + '';

        videoLayers.push(
            new VideoLayerClass(
                i,
                layer.first() as HTMLElement,
                zIndex,
                playlist.playlist.template_name,
                playlist.playlist.map_detail || 'country',
                onState,
                client,
            ),
        );

        videoLayersElm.append(layer);
    }

    console.log('layers', videoLayers);

    audioLayer = new AudioLayerClass(u('#audio').first() as HTMLElement, -100);
    if (playlist.hasAudio()) {
        await audioLayer.setSource('#audio', playlist.getAudioUrl());
        audioLayer.setActive(true, () => {});
    }

    let source = await playlist.getNextContent();
    if (source === null) {
        console.error('failed to fill first layer with a source');
        return;
    }

    await videoLayers[0].setSource('#first', source);

    if (debug) {
        u('body').append(
            `<div id="debug" style="position:absolute; top:0; left:0; z-index:5000; background-color:black; opacity:0.6; padding:1em">DEBUG</div>`,
        );

        setInterval(() => {
            const debug = u('#debug').empty();
            for (let i = 0; i < videoLayers.length; i++) {
                const status = videoLayers[i].getStatus();
                debug.append(
                    `<div>${status.name} ${status.state} ${
                        status.active ? 'active' : 'inactive'
                    } ${status.vod ? 'vod' : 'live'} z${status.zindex} ${
                        status.source.length > 256
                            ? status.source.slice(0, 160) + '...'
                            : status.source
                    }</div>`,
                );
            }
        }, 500);
    }

    return;
};

await main();
